/**
 * @license
 * Copyright 2023 Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
import { getShadowElement, deepQuerySelectorAll, getActiveElement, toggleAttribute } from '@tylertech/forge-core';
import { BaseAdapter } from '../core/base/base-adapter';
import { AUTOCOMPLETE_CONSTANTS } from './autocomplete-constants';
import { TEXT_FIELD_CONSTANTS } from '../text-field';
import { ListDropdown } from '../list-dropdown';
import { CHIP_FIELD_CONSTANTS } from '../chip-field';
import { POPUP_CONSTANTS } from '../popup';
import { setAriaControls, tryCreateAriaControlsPlaceholder } from '../core';
/**
 * The DOM adapter for the `<forge-autocomplete>` element.
 */
export class AutocompleteAdapter extends BaseAdapter {
    constructor(component) {
        super(component);
        this.setInputElement();
    }
    get inputElement() {
        return this._inputElement;
    }
    setInputElement() {
        const inputElements = deepQuerySelectorAll(this._component, AUTOCOMPLETE_CONSTANTS.selectors.INPUT, false);
        if (inputElements.length) {
            this._inputElement = inputElements[0];
        }
        return this._inputElement;
    }
    setInputAttribute(name, value) {
        this._inputElement.setAttribute(name, value);
    }
    addInputListener(type, listener) {
        this._inputElement.addEventListener(type, listener);
    }
    removeInputListener(type, listener) {
        this._inputElement.removeEventListener(type, listener);
    }
    initializeInputAccessibility(identifier) {
        this._inputElement.setAttribute('autocomplete', 'off');
        this._inputElement.setAttribute('role', 'combobox');
        this._inputElement.setAttribute('aria-live', 'polite');
        this._inputElement.setAttribute('aria-atomic', 'true');
        this._inputElement.setAttribute('aria-haspopup', 'true');
        this._inputElement.setAttribute('aria-expanded', 'false');
        this._inputElement.setAttribute('aria-autocomplete', 'list');
        tryCreateAriaControlsPlaceholder();
        setAriaControls(this._inputElement);
    }
    isWrappingChipField() {
        return !!this._component.querySelector(CHIP_FIELD_CONSTANTS.elementName);
    }
    show(config, popupTarget) {
        this._targetElement = this._getTargetElement(popupTarget);
        if (!this._targetElement) {
            return;
        }
        this._listDropdown = new ListDropdown(this._targetElement, config);
        this._listDropdown.open();
        this._inputElement.setAttribute('aria-expanded', 'true');
        this._inputElement.setAttribute('aria-controls', `list-dropdown-popup-${config.id}`);
        this._tryToggleDropdownIconRotation(true);
    }
    hide(listener) {
        if (!this._listDropdown) {
            return;
        }
        const { targetElement } = this._listDropdown.dropdownElement;
        targetElement.removeEventListener(POPUP_CONSTANTS.events.BLUR, listener);
        this.setBusyVisibility(false);
        this._tryToggleDropdownIconRotation(false);
        this._inputElement.removeAttribute('aria-activedescendant');
        this._inputElement.removeAttribute('aria-controls');
        this._inputElement.setAttribute('aria-expanded', 'false');
        this._listDropdown.close();
        this._listDropdown = undefined;
    }
    setBusyVisibility(isVisible) {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.setBusyVisibility(isVisible);
    }
    setDismissListener(listener) {
        if (!this._listDropdown || !this._listDropdown.dropdownElement) {
            return;
        }
        const dropdownElement = this._listDropdown.dropdownElement;
        if (dropdownElement.targetElement) {
            dropdownElement.targetElement.addEventListener(POPUP_CONSTANTS.events.BLUR, listener);
        }
    }
    focus() {
        window.requestAnimationFrame(() => this._inputElement.focus());
    }
    setOptions(options) {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.setOptions(options);
    }
    appendOptions(options) {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.appendOptions(options);
    }
    setSelectedText(value) {
        this._inputElement.value = value;
    }
    getInputValue() {
        return this._inputElement.value;
    }
    setInputValue(value) {
        this._inputElement.value = value;
    }
    selectInputValue() {
        window.requestAnimationFrame(() => this._inputElement.select());
    }
    isFocusWithinPopup(target) {
        if (!this._listDropdown || !this._listDropdown.dropdownElement) {
            return false;
        }
        return this._listDropdown.dropdownElement.contains(target);
    }
    hasFocus() {
        const activeElement = getActiveElement(this._component.ownerDocument);
        return activeElement === this._inputElement || this.isFocusWithinPopup(activeElement);
    }
    hasInputElement() {
        return !!this._inputElement;
    }
    setDropdownIconListener(type, listener) {
        window.requestAnimationFrame(() => {
            const dropdownIcon = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.DROPDOWN_ICON);
            if (dropdownIcon) {
                dropdownIcon.addEventListener(type, listener);
            }
        });
    }
    removeDropdownIconListener(type, listener) {
        const dropdownIcon = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.DROPDOWN_ICON);
        if (dropdownIcon) {
            dropdownIcon.removeEventListener(type, listener);
        }
    }
    setClearButtonListener(type, listener) {
        window.requestAnimationFrame(() => {
            const clearButton = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.CLEAR_BUTTON);
            if (clearButton) {
                clearButton.addEventListener(type, listener);
            }
        });
    }
    removeClearButtonListener(type, listener) {
        const clearButton = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.CLEAR_BUTTON);
        if (clearButton) {
            clearButton.removeEventListener(type, listener);
        }
    }
    propagateKey(key) {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.handleKey(key);
    }
    updateActiveDescendant(id) {
        if (!this._targetElement) {
            return;
        }
        toggleAttribute(this._inputElement, !!id, 'aria-activedescendant', id);
    }
    getTargetElementWidth(selector) {
        if (!this._targetElement) {
            this._targetElement = this._getTargetElement(selector);
        }
        return this._targetElement.getBoundingClientRect().width;
    }
    getPopupElement() {
        var _a, _b;
        return (_b = (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.dropdownElement) !== null && _b !== void 0 ? _b : null;
    }
    activateFirstOption() {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.activateFirstOption();
    }
    activateSelectedOption() {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.activateSelectedOption();
    }
    activateOptionByIndex(value) {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.activateOption(value);
    }
    getActiveOptionIndex() {
        var _a, _b;
        return (_b = (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.getActiveOptionIndex()) !== null && _b !== void 0 ? _b : null;
    }
    clearActiveOption() {
        var _a;
        (_a = this._listDropdown) === null || _a === void 0 ? void 0 : _a.clearActiveOption();
    }
    setSelectedOptions(options) {
        if (this._listDropdown) {
            const values = options.map(o => o.value);
            this._listDropdown.setSelectedValues(values);
        }
    }
    queueDropdownPositionUpdate() {
        if (!this.getPopupElement()) {
            return;
        }
        // We need to wait for the next animation frame to ensure that the layout has been updated
        window.requestAnimationFrame(() => {
            const dropdownEl = this.getPopupElement();
            dropdownEl === null || dropdownEl === void 0 ? void 0 : dropdownEl.position();
        });
    }
    _getTargetElement(selector) {
        return selector ? this._component.querySelector(selector) || this._getDefaultTargetElement() : this._getDefaultTargetElement();
    }
    _getDefaultTargetElement() {
        // This component is often used with the text-field, if so, let's target our popup around one if its internal elements for proper alignnment
        const textField = this._component.querySelector('forge-text-field');
        if (textField && textField.shadowRoot) {
            const textFieldRoot = getShadowElement(textField, TEXT_FIELD_CONSTANTS.selectors.ROOT);
            if (textFieldRoot) {
                return textFieldRoot;
            }
        }
        // This component is often used with the chip-field, if so, let's target our popup around one if its internal elements for proper alignnment
        const chipField = this._component.querySelector('forge-chip-field');
        if (chipField && chipField.shadowRoot) {
            const chipFieldRoot = getShadowElement(chipField, CHIP_FIELD_CONSTANTS.selectors.ROOT);
            if (chipFieldRoot) {
                return chipFieldRoot;
            }
        }
        return this._component.querySelector('input') || this._component;
    }
    _tryToggleDropdownIconRotation(state) {
        const dropdownIcon = this._component.querySelector(AUTOCOMPLETE_CONSTANTS.selectors.DROPDOWN_ICON);
        if (dropdownIcon) {
            toggleAttribute(dropdownIcon, state, AUTOCOMPLETE_CONSTANTS.attributes.DROPDOWN_ICON_OPEN);
        }
    }
}
